import React, { FC, useState } from 'react';
import { Breadcrumb, RadioChangeEvent } from 'antd';
import { ButtonGroup, ButtonStroke, InputNumeric } from '@blink/components';
import { ReactComponent as OtherIcon } from '@blink/assets/src/icons/other.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { concatClasses } from '@blink/components/src/utils';
import * as yup from 'yup';
import { ButtonSize } from '@blink/components/src/Button/Button.types';

import { usePersonalisedPoolStyle } from './PersonalisedPool.style';
import { PATHS } from 'src/routes/paths';

import personalisedPoolSchema, {
    PersonalisedPoolData,
} from '@/shared/validations/personalisedPool.schema';
import { PoolRequest, postPersonalisedPool, TYPE } from '@/shared/api/borrow';
import { toast } from '@/shared/hooks/useToast';
import {
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbSeparator,
    TooltipWrapper,
    Textarea,
    AssetWithIcon,
} from '@/shared/ui';
import { withCharCounter } from '@/shared/hoc/withCharCounter';

enum Token {
    WETH = 'WETH',
    USDT = 'USDT',
    USDC = 'USDC',
    WBTC = 'WBTC',
    USD0 = 'USD0',
    OTHER = 'Other',
}

const groupList = [
    {
        text: `${TYPE.LEND.charAt(0).toUpperCase()}${TYPE.LEND.slice(1).toLowerCase()}`,
        value: TYPE.LEND,
    },
    {
        text: `${TYPE.BORROW.charAt(0).toUpperCase()}${TYPE.BORROW.slice(1).toLowerCase()}`,
        value: TYPE.BORROW,
    },
];

const tokenList = [
    { value: Token.WETH, text: Token.WETH },
    { value: Token.USDT, text: Token.USDT },
    { value: Token.USDC, text: Token.USDC },
    { value: Token.WBTC, text: Token.WBTC },
];

const TextareaWithCounter = withCharCounter(Textarea);

export const PersonalisedPool: FC = () => {
    const classes = usePersonalisedPoolStyle();
    const navigate = useNavigate();
    const location = useLocation();
    const referrer = location.state?.referrer || PATHS.HOME;

    const [formState, setFormState] = useState<PersonalisedPoolData>({
        type: referrer === `/${TYPE.BORROW}` ? TYPE.BORROW : TYPE.LEND,
        token: Token.WETH,
        position: '',
        apy: '',
        details: '',
    });

    const [errors, setErrors] = useState<Partial<Record<keyof PersonalisedPoolData, string>>>({});

    const handleChange = (
        e:
            | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            | string
            | RadioChangeEvent
            | number,
        field: keyof PersonalisedPoolData,
    ) => {
        if (e === null) {
            setFormState((prevState) => ({ ...prevState, [field]: null }));
            return;
        }
        const value = typeof e === 'string' || typeof e === 'number' ? e : e.target.value;
        setFormState((prevState) => ({ ...prevState, [field]: value }));
    };

    const handleSubmit = async () => {
        try {
            setErrors({});
            const validatedData: PoolRequest = await personalisedPoolSchema.validate(formState, {
                abortEarly: false,
            });

            const result = await postPersonalisedPool(validatedData);

            if (result === 'null') {
                navigate(PATHS.POOL_PERSONALISED_SUCCESS);
            }
            toast({
                title: 'Request submitted successfully!',
                variant: 'success',
            });
        } catch (validationError) {
            if (validationError instanceof yup.ValidationError) {
                const validationErrors = validationError.inner.reduce(
                    (acc, err) => ({ ...acc, [err.path!]: err.message }),
                    {},
                );
                setErrors(validationErrors);
            }
        }
    };

    return (
        <div>
            <div className={classes.top}>
                <div className={classes.breadcrumbs}>
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href={PATHS.HOME}>Investments</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbLink href={PATHS.POOL_PERSONALISED}>
                                    <div className='font-semibold text-content-primary'>
                                        Request personalised pool
                                    </div>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>
                <div className={classes.heading}>
                    <div className={classes.title}>
                        <div className={classes.titleText}>Request Personalised Pool</div>
                        <div className={classes.description}>
                            Submit your request. Choose a token, size, APY, and provide any relevant
                            details for review.
                        </div>
                    </div>
                </div>
            </div>

            <div className={classes.content}>
                <div className={classes.param}>
                    <ButtonGroup
                        className={classes.group}
                        items={groupList}
                        value={formState.type}
                        handleChange={(value) => handleChange(value, 'type')}
                    />
                </div>
                <div className={classes.param}>
                    <div className={classes.btnWrapper}>
                        {tokenList.map((token) => (
                            <ButtonStroke
                                isOutline
                                className={concatClasses(
                                    classes.tokenBtn,
                                    token.value === formState.token ? 'active' : '',
                                )}
                                key={token.value}
                                onClick={() => handleChange(token.value, 'token')}
                            >
                                <AssetWithIcon assetName={token.value} />
                            </ButtonStroke>
                        ))}
                        <ButtonStroke
                            isOutline
                            className={concatClasses(
                                classes.tokenBtn,
                                Token.OTHER === formState.token ? 'active' : '',
                            )}
                            onClick={() => handleChange(Token.OTHER, 'token')}
                        >
                            <OtherIcon className='icon' />
                            Other
                        </ButtonStroke>
                    </div>
                </div>
                <div className={classes.param}>
                    <div className={classes.label}>
                        <div className={classes.title}>Size position</div>
                        <TooltipWrapper text='Enter the amount of the asset you want to lend or borrow. Make sure the value meets the platform’s minimum requirements.' />
                    </div>

                    <InputNumeric
                        value={formState.position}
                        onChange={(value) => handleChange(value!, 'position')}
                        placeholder='0.00'
                        className={concatClasses(classes.input, errors.position ? 'error' : '')}
                    />
                    {errors.position && <div className={classes.error}>{errors.position}</div>}
                </div>

                <div className={classes.param}>
                    <div className={classes.label}>
                        <div className={classes.title}>Desired APY</div>
                        <TooltipWrapper text='Specify the annual percentage yield (APY) you aim for based on your lending or borrowing strategy.' />
                    </div>

                    <InputNumeric
                        value={formState.apy}
                        onChange={(value) => handleChange(value!, 'apy')}
                        placeholder='0%'
                        className={concatClasses(classes.input, errors.apy ? 'error' : '')}
                        length={4}
                        suffix='%'
                    />
                    {errors.apy && <div className={classes.error}>{errors.apy}</div>}
                </div>

                <div className={concatClasses(classes.param, classes.textArea)}>
                    <div className={classes.title}>Additional details *</div>
                    <TextareaWithCounter
                        maxLength={1000}
                        style={{ height: 128, width: 680, resize: 'none' }}
                        value={formState.details}
                        onChange={(value) => handleChange(value, 'details')}
                        placeholder={`Include any additional information that may be relevant to your request`}
                        className={concatClasses(
                            classes.input,
                            classes.textWrapper,
                            errors.details ? 'error' : '',
                        )}
                    />
                    {errors.details && <div className={classes.error}>{errors.details}</div>}
                    {formState.token === Token.OTHER && (
                        <div className={classes.text}>
                            Provide detailed information, including troubleshooting steps. If
                            selecting another token, include the token name in the details.
                        </div>
                    )}
                </div>

                <div className={classes.btn}>
                    <ButtonStroke isPrimary onClick={handleSubmit} size={ButtonSize.md}>
                        Submit Request
                    </ButtonStroke>
                </div>
            </div>
        </div>
    );
};
